import { Pie, RejectIcon, SendReminder } from '@icons';

import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { SelectedFilterValues } from '@components/multi-field-filter/types';
import { TimeZones } from '@constants/date';
import { DEFAULT_PLACEHOLDER, NULLDATE, OrgRoleType } from '@constants/generic';
import {
  getCurrentFinancialYear,
  getLastFinancialYear
} from '@features/reports/helper';
import {
  DateRange,
  MarkAsPaidOptionsInterface,
  MemberTransactionType,
  PaidOnDetails,
  SettlementDetails
} from '@types';
import {
  formatISODate,
  getFormattedDateTimeWithOffset,
  getStartAndEndDateOfLastMonth,
  getStartAndPresentDateOfCurrentMonth
} from '@utils/date';
import { DropdownOption } from 'types/dropdown';

import PaidOnDetailsTooltip from './components/paidon-details/PaidOnDetails';
import SettlementDetailsTooltip from './components/settlement-details/SettlementDetails';
import { TransactionModalContext, TransactionStatus } from './constants';
import { CustomField } from './types';

export const getSettledOn = (
  id: string,
  status: string,
  settlementStatus: string,
  payment_mode: string,
  settledOn: string,
  settlementDetails?: SettlementDetails[]
) => {
  // check atleas one settlement detail has a date and that is not NULLDATE
  const hasSettledOn = settlementDetails?.some(
    (detail) => detail.settled_at && detail.settled_at !== NULLDATE
  );

  if (settlementDetails && settlementDetails.length > 0) {
    if (hasSettledOn) {
      if (settlementDetails.length === 1) {
        return formatISODate({
          date: settlementDetails[0].settled_at,
          format: 'dd-mm-yyyy'
        });
      }
      return (
        <SettlementDetailsTooltip
          id={id}
          settlementDetails={settlementDetails}
        />
      );
    }
    return <div className="text-grey">Processing</div>;
  }

  // manual payment
  if (payment_mode === 'manual') return DEFAULT_PLACEHOLDER;
  // intermediate state, paid but not settled
  if (status === 'success' && settlementStatus === '')
    return <div className="text-grey">Processing</div>;
  // payment settled
  if (status === 'success' && settlementStatus === 'settled')
    return formatISODate({ date: settledOn, format: 'dd-mm-yyyy' });

  return DEFAULT_PLACEHOLDER;
};

export const getPaidOn = (
  id: string,
  paidAt: string,
  paidOnDetails?: PaidOnDetails[]
) => {
  // check atleas one settlement detail has a date and that is not NULLDATE
  const hasPaidOn = paidOnDetails?.some(
    (detail) => detail.paid_at && detail.paid_at !== NULLDATE
  );

  if (paidOnDetails && paidOnDetails.length > 0) {
    if (hasPaidOn) {
      if (paidOnDetails.length === 1) {
        return formatISODate({
          date: paidOnDetails[0].paid_at,
          format: 'dd-mm-yyyy'
        });
      }
      return <PaidOnDetailsTooltip id={id} paidOnDetails={paidOnDetails} />;
    }
    return DEFAULT_PLACEHOLDER;
  }

  if (paidAt) return formatISODate({ date: paidAt, format: 'dd-mm-yyyy' });

  return DEFAULT_PLACEHOLDER;
};

export const getPaymentDetails = (memberDetails: MemberTransactionType) => [
  ...(!memberDetails.fee_amount_configuration
    ? [
        {
          title: 'Total Amount Requested',
          value: parseFloat(
            (
              Number(memberDetails.total_amount) +
              Number(memberDetails.closed_amount) +
              Number(memberDetails.total_discount_amount ?? 0)
            ).toString()
          ).toFixed(2),
          subValue: parseFloat(memberDetails.total_amount).toFixed(2)
        }
      ]
    : []),
  ...(Number(memberDetails.total_paid_amount) !== 0 // do not show for fully pending case
    ? [
        {
          title: 'Total Amount Paid',
          value: parseFloat(memberDetails.total_paid_amount).toFixed(2),
          status: 'Paid',
          ...(Number(memberDetails.total_paid_amount) >
            Number(memberDetails.total_settled_amount) && {
            subText: 'Total amount paid includes settled and processing'
          })
        }
      ]
    : []),
  ...(memberDetails.total_discount_amount &&
  parseFloat(memberDetails.total_discount_amount) > 0
    ? [
        {
          title: 'Total Amount Discounted',
          value: parseFloat(memberDetails.total_discount_amount).toFixed(2),
          status: 'Discount'
        }
      ]
    : []),
  // show only if there is pending amount
  ...(Number(memberDetails.total_amount) !==
  Number(memberDetails.total_paid_amount)
    ? [
        {
          title: 'Remaining Balance',
          value: parseFloat(
            (
              Number(memberDetails.total_amount) -
              Number(memberDetails.total_paid_amount)
            ).toString()
          ).toFixed(2),
          status: 'Pending',
          dueDate: memberDetails.due_at
            ? `Requested on ${formatISODate({
                date: memberDetails.due_at,
                format: 'DD MMM YYYY'
              })}`
            : DEFAULT_PLACEHOLDER
        }
      ]
    : []),
  ...(Number(memberDetails.closed_amount) !== 0
    ? [
        {
          title: 'Remaining Amount',
          value:
            memberDetails.closed_amount &&
            parseFloat(memberDetails.closed_amount).toFixed(2),
          status: 'Closed',
          dueDate: memberDetails.closed_at
            ? `${formatISODate({
                date: memberDetails.closed_at,
                format: 'MMM DD YYYY'
              })}`
            : DEFAULT_PLACEHOLDER,
          subText: memberDetails.close_payment_note
            ? memberDetails.close_payment_note
            : DEFAULT_PLACEHOLDER
        }
      ]
    : [])
];

/** Human-readable payment channel (offline / online), single line. */
export const getPaymentChannelDescription = (
  payment_mode: string,
  payment_method: string | null | undefined
) => {
  const method = payment_method ?? '';
  if (payment_mode === 'manual') {
    if (method === '' || method === null) return 'Paid Offline';
    if (method === 'Cash') return 'Paid Offline via Cash';
    return `Paid Offline via ${method}`;
  }
  if (payment_mode === 'automatic') {
    if (method === 'mandate') return 'Paid via CoFee Autodebit';
    return 'Paid via Cofee';
  }
  return '';
};

export const getPaymentStatusDisplayText = (
  status: TransactionStatus,
  payment_mode: string,
  payment_method: string
) => {
  if (status === TransactionStatus.TO_SETTLE) return 'Processing';
  if (status === TransactionStatus.SETTLED) {
    return getPaymentChannelDescription(payment_mode, payment_method);
  }
  if (status === TransactionStatus.PROCCESSING) return 'Pending';
  return '';
};

export const getTransactionStatus = (
  status: string,
  settlementStatus: string
) => {
  if (status === 'disabled') return TransactionStatus.DISABLED;
  if (status === 'success' && settlementStatus === '')
    return TransactionStatus.TO_SETTLE; // blue
  if (status === 'success' && settlementStatus === 'settled')
    return TransactionStatus.SETTLED; // green
  if (status === 'processing' && settlementStatus === '')
    return TransactionStatus.PROCCESSING; // yellow
  return TransactionStatus.DEFAULT;
};

export const getQuickCollectInfo = (memberDetails: MemberTransactionType) =>
  [
    {
      title: 'Status',
      value: getPaymentStatusDisplayText(
        getTransactionStatus(
          memberDetails.status,
          memberDetails.settlement_status
        ),
        memberDetails.payment_mode,
        memberDetails.payment_method
      )
    },
    ...(memberDetails.branch_name
      ? [
          {
            title: 'Branch',
            value: memberDetails.branch_name
          }
        ]
      : []),
    {
      title: 'Requested on',
      value: formatISODate({
        date: memberDetails.due_at,
        format: 'DD MMM YYYY'
      })
    },
    { title: 'Payment To', value: memberDetails.payment_to },
    // { title: 'Paid By', value: memberDetails.name },
    { title: 'Transaction ID', value: memberDetails.id },
    // ...(memberDetails.settlement_status !== 'settled'
    //   ? [{ title: 'Paid Via', value: memberDetails.payment_method }]
    //   : []),
    { title: 'Notes', value: memberDetails.payment_description },
    {
      title: 'Settled On',
      value: formatISODate({
        date: memberDetails.settled_at,
        format: 'DD MMM YYYY at hh:mm am/pm'
      })
    },
    ...(memberDetails.utr && memberDetails.payment_mode !== 'manual'
      ? [{ title: 'UTR Number', value: memberDetails.utr }]
      : []),
    ...(memberDetails.paid_at
      ? [
          {
            title: 'Paid On',
            value: formatISODate({
              date: memberDetails.paid_at,
              format:
                memberDetails.payment_mode === 'automatic'
                  ? 'DD MMM YYYY at hh:mm am/pm'
                  : 'DD MMM YYYY'
            })
          }
        ]
      : []),
    ...(memberDetails.marked_paid_at
      ? [
          {
            title: 'Marked On',
            value: formatISODate({
              date: memberDetails.marked_paid_at,
              format: 'DD MMM YYYY at hh:mm am/pm'
            })
          }
        ]
      : []),
    ...(memberDetails.member_id
      ? [
          {
            title: 'Member ID',
            value: memberDetails.member_id
          }
        ]
      : []),
    ...(memberDetails.merchant_order_id
      ? [
          {
            title: 'Merchant Order ID',
            value: memberDetails.merchant_order_id
          }
        ]
      : [])
  ].filter((info) => info.value);

export const getTransactionModalData = (
  handleDisableTransaction: () => {},
  handleSendReminder: () => {},
  handleEnablePartialPayment: () => {},
  status: 'lite' | 'cofee'
) => ({
  [TransactionModalContext.DISABLE_PAYMENT]: {
    title: `Disable Payment ${status === 'lite' ? 'Request' : 'Link'}?`,
    message: `Are you sure, you want to disable this payment ${
      status === 'lite' ? 'request' : 'link'
    } permanently?`,
    confirmButtonLabel: 'Disable',
    confirmButtonStyle: '!text-errorLite3',
    ModalIcon: RejectIcon,
    onConfirm: handleDisableTransaction
  },
  [TransactionModalContext.SEND_REMINDER]: {
    title: 'Send Reminder',
    message: 'On confirming reminder will be send',
    confirmButtonLabel: 'Send',
    confirmButtonStyle: '',
    ModalIcon: SendReminder,
    onConfirm: handleSendReminder
  },
  [TransactionModalContext.ENABLE_PARTIAL_PAYMENT]: {
    title: 'Enable partial payment ?',
    message:
      'Once enabled, users will be able to make partial payment for this payment request ',
    confirmButtonLabel: 'Confirm',
    confirmButtonStyle: 'text-primary',
    ModalIcon: Pie,
    onConfirm: handleEnablePartialPayment
  }
});

export const getModalTitleAndSubText = (
  minAmount: number,
  pendingAmount: number,
  transaction: MarkAsPaidOptionsInterface
) => {
  if (!transaction.is_split_payment) {
    return {
      title: (
        <p>
          Mark{' '}
          <span className="inline-flex items-baseline">
            <CurrencyAmount value={transaction.amount} className="gap-0.5" />
          </span>{' '}
          as paid?
        </p>
      ),
      subText: (
        // I want to show this in a line
        <p>
          Are you sure you want to mark the receipt of{' '}
          <span className="inline-flex items-baseline">
            <CurrencyAmount value={transaction.amount} className="gap-0.5" />
          </span>{' '}
          from {transaction.name} as paid?
        </p>
      )
    };
  }
  if (transaction.is_split_payment && pendingAmount <= minAmount) {
    return {
      title: (
        <p>
          Mark{' '}
          <span className="inline-flex items-baseline">
            <CurrencyAmount value={pendingAmount} className="gap-0.5" />
          </span>{' '}
          as paid?
        </p>
      ),
      subText: (
        <p>
          Are you sure you want to mark the receipt of{' '}
          <span className="inline-flex items-baseline">
            <CurrencyAmount value={pendingAmount} className="gap-0.5" />
          </span>{' '}
          from {transaction.name} as paid?
        </p>
      )
    };
  }
  if (transaction.is_split_payment) {
    return {
      title: 'Mark as paid?',
      subText: (
        <p>
          Are you sure you want to mark the receipt of{' '}
          <span className="inline-flex items-baseline">
            <CurrencyAmount value={transaction.amount} className="gap-0.5" />
          </span>{' '}
          from {transaction.name} as paid?
        </p>
      )
    };
  }
  return {
    title: '',
    subText: ''
  };
};

export const getStatusColors = (status: string) => {
  if (status === 'Processing') {
    return {
      textColor: '!text-blue1', // blue
      bgColor: 'bg-blue'
    };
  }
  if (
    (typeof status === 'string' && status.includes('Paid Offline')) ||
    status === 'Paid via Cofee' ||
    status === 'Paid via CoFee Autodebit'
  ) {
    return {
      textColor: 'text-primary40', // green
      bgColor: 'bg-primary10'
    };
  }
  if (status === 'Pending') {
    return {
      textColor: 'text-yellow70', // yellow
      bgColor: 'bg-yellowLite4'
    };
  }
  return {
    textColor: '',
    bgColor: ''
  };
};

export const getAllTransactionTabActions = (
  row: any,
  hasPaymentOrderEditAccess: boolean,
  enableDiscountInUpdateDueModal: boolean,
  onClickMarkAsPaid: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickUpdateDue: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  hasSendReminderAccess: boolean,
  onClickSendReminder: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickClosePayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  handleCopy: (e: React.MouseEvent, link: string) => void,
  hasEnablePartialPaymentAccess: boolean,
  splitRequiredMinAmount: string,
  onClickEnablePartialPayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickDisable: (event: React.MouseEvent, row: MemberTransactionType) => void,
  isAutoPayInProgress?: boolean,
  isRestrictDisablePaymentOrderEnabled?: boolean,
  userRole?: string,
  isOrgLite?: boolean
) => {
  const isOwnerOrOrgAdmin =
    userRole === OrgRoleType.OWNER ||
    userRole === OrgRoleType.ORGANISATION_ADMIN ||
    userRole === OrgRoleType.INDIVIDUAL;

  const showDisableIcon = (item: MemberTransactionType) =>
    item?.status === 'processing' &&
    item.total_paid_amount === '0' &&
    hasPaymentOrderEditAccess &&
    (!isRestrictDisablePaymentOrderEnabled || isOwnerOrOrgAdmin);

  const showEnablePartialPayment =
    hasEnablePartialPaymentAccess &&
    !row.is_split_payment &&
    (row.is_custom_term ||
      Number(row.total_amount) >= Number(splitRequiredMinAmount));

  const showToSendReminder =
    hasSendReminderAccess &&
    (row.remind_again || (row.reminder_count_exceeded && !row.remind_again));
  const showReminderAlreadySent =
    hasSendReminderAccess && !row.remind_again && !row.reminder_count_exceeded;

  const closePaymentAccess =
    hasPaymentOrderEditAccess &&
    row.is_split_payment &&
    row.total_paid_amount !== '0';

  const showUpdateDue =
    enableDiscountInUpdateDueModal &&
    !(
      Number(row.total_paid_amount) === 0 &&
      Number(row.total_amount) - Number(row.total_paid_amount) < 100
    );

  const disabledTooltip = (item: MemberTransactionType) => {
    if (isAutoPayInProgress) return 'Autopay in progress';
    if (item.reminder_count_exceeded)
      return 'Reminder cannot be sent as the maximum reminder limit has been reached.\nPlease share the payment link manually';
    return undefined;
  };

  const actions = [
    {
      ...(hasPaymentOrderEditAccess && {
        id: `markAsPaid-${row.id}`,
        label: 'Mark As Paid',
        onClick: (event: React.MouseEvent) => onClickMarkAsPaid(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-primary'
      })
    },
    {
      ...(hasPaymentOrderEditAccess &&
        showUpdateDue && {
          id: `updateDue-${row.id}`,
          label: 'Update Due Amount',
          onClick: (event: React.MouseEvent) => onClickUpdateDue(event, row),
          isDisabled: row.status === 'disabled' || isAutoPayInProgress,
          disabledTooltip: isAutoPayInProgress
            ? 'Autopay in progress'
            : undefined
        })
    },
    {
      ...(closePaymentAccess &&
        !enableDiscountInUpdateDueModal && {
          id: `closePayment-${row.id}`,
          label: 'Close Payment',
          onClick: (event: React.MouseEvent) => onClickClosePayment(event, row),
          isDisabled: row.status === 'disabled' || isAutoPayInProgress,
          disabledTooltip: isAutoPayInProgress
            ? 'Autopay in progress'
            : undefined,
          style: 'text-error40'
        })
    },
    {
      ...(showToSendReminder && {
        id: `sendReminder-${row.id}`,
        label: 'Send Reminder',
        onClick: (event: React.MouseEvent) => onClickSendReminder(event, row),
        isDisabled:
          row.status === 'disabled' ||
          isAutoPayInProgress ||
          row.reminder_count_exceeded,
        disabledTooltip: disabledTooltip(row),
        showWarningIcon: !!row.reminder_count_exceeded
      })
    },
    {
      ...(showReminderAlreadySent && {
        id: `reminderSent-${row.id}`,
        label: 'Reminder Sent',
        onClick: (event: React.MouseEvent) =>
          handleCopy(event, row?.payment_link),
        isDisabled: true
      })
    },
    {
      ...(showEnablePartialPayment && {
        id: `enablePartialPayment-${row.id}`,
        label: 'Enable Partial Payment',
        onClick: (event: React.MouseEvent) =>
          onClickEnablePartialPayment(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-yellow60'
      })
    },
    {
      ...(row?.payment_link && {
        id: `copyPaymentLink-${row.id}`,
        label: isOrgLite ? 'Copy Payment Message' : 'Copy Payment Link',
        onClick: (event: React.MouseEvent) =>
          handleCopy(event, row?.payment_link),
        iconName: 'copy-primary',
        tooltipText: 'Copied!',
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress ? 'Autopay in progress' : undefined
      })
    },
    {
      ...(showDisableIcon(row) && {
        id: `disable-${row.id}`,
        label: 'Disable',
        onClick: (event: React.MouseEvent) => onClickDisable(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-error40'
      })
    }
  ];

  const isActionButtonDisabled =
    row.status === 'disabled' ||
    (!hasPaymentOrderEditAccess &&
      !showToSendReminder &&
      !showReminderAlreadySent &&
      !showEnablePartialPayment &&
      !showDisableIcon(row));

  return { actions, isActionButtonDisabled };
};

export const getPendingTransactionTabActions = (
  row: any,
  hasPaymentOrderEditAccess: boolean,
  enableDiscountInUpdateDueModal: boolean,
  onClickMarkAsPaid: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickUpdateDue: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickClosePayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  hasSendReminderAccess: boolean,
  onClickSendReminder: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  handleCopy: (e: React.MouseEvent, link: string) => void,
  hasEnablePartialPaymentAccess: boolean,
  splitRequiredMinAmount: string,
  onClickEnablePartialPayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickDisable: (event: React.MouseEvent, row: MemberTransactionType) => void,
  isAutoPayInProgress?: boolean,
  isRestrictDisablePaymentOrderEnabled?: boolean,
  userRole?: string,
  isOrgLite?: boolean
) => {
  const isOwnerOrOrgAdmin =
    userRole === OrgRoleType.OWNER ||
    userRole === OrgRoleType.ORGANISATION_ADMIN ||
    userRole === OrgRoleType.INDIVIDUAL;

  const showDisableIcon = (item: MemberTransactionType) =>
    item?.status === 'processing' &&
    item.total_paid_amount === '0' &&
    hasPaymentOrderEditAccess &&
    (!isRestrictDisablePaymentOrderEnabled || isOwnerOrOrgAdmin);

  const showEnablePartialPayment =
    hasEnablePartialPaymentAccess &&
    !row.is_split_payment &&
    (row.is_custom_term ||
      Number(row.total_amount) >= Number(splitRequiredMinAmount));

  const showToSendReminder =
    hasSendReminderAccess &&
    (row.remind_again || (row.reminder_count_exceeded && !row.remind_again));
  const showReminderAlreadySent =
    hasSendReminderAccess && !row.remind_again && !row.reminder_count_exceeded;

  const closePaymentAccess =
    hasPaymentOrderEditAccess &&
    row.is_split_payment &&
    row.total_paid_amount !== '0';

  const showUpdateDue =
    enableDiscountInUpdateDueModal &&
    !(
      Number(row.total_paid_amount) === 0 &&
      Number(row.total_amount) - Number(row.total_paid_amount) < 100
    );

  const disabledTooltip = (item: MemberTransactionType) => {
    if (isAutoPayInProgress) return 'Autopay in progress';
    if (item.reminder_count_exceeded)
      return 'Reminder cannot be sent as the maximum reminder limit has been reached.\nPlease share the payment link manually';
    return undefined;
  };

  const actions = [
    {
      ...(hasPaymentOrderEditAccess && {
        id: `markAsPaid-${row.id}`,
        label: 'Mark As Paid',
        onClick: (event: React.MouseEvent) => onClickMarkAsPaid(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-primary'
      })
    },
    {
      ...(hasPaymentOrderEditAccess &&
        showUpdateDue && {
          id: `updateDue-${row.id}`,
          label: 'Update Due Amount',
          onClick: (event: React.MouseEvent) => onClickUpdateDue(event, row),
          isDisabled: row.status === 'disabled' || isAutoPayInProgress,
          disabledTooltip: isAutoPayInProgress
            ? 'Autopay in progress'
            : undefined
        })
    },
    {
      ...(closePaymentAccess &&
        !enableDiscountInUpdateDueModal && {
          id: `closePayment-${row.id}`,
          label: 'Close Payment',
          onClick: (event: React.MouseEvent) => onClickClosePayment(event, row),
          isDisabled: row.status === 'disabled' || isAutoPayInProgress,
          disabledTooltip: isAutoPayInProgress
            ? 'Autopay in progress'
            : undefined,
          style: 'text-error40'
        })
    },
    {
      ...(showToSendReminder && {
        id: `sendReminder-${row.id}`,
        label: 'Send Reminder',
        onClick: (event: React.MouseEvent) => onClickSendReminder(event, row),
        isDisabled:
          row.status === 'disabled' ||
          isAutoPayInProgress ||
          row.reminder_count_exceeded,
        disabledTooltip: disabledTooltip(row),
        showWarningIcon: !!row.reminder_count_exceeded
      })
    },
    {
      ...(showReminderAlreadySent && {
        id: `reminderSent-${row.id}`,
        label: 'Reminder Sent',
        onClick: (event: React.MouseEvent) =>
          handleCopy(event, row?.payment_link),
        isDisabled: true
      })
    },
    {
      ...(showEnablePartialPayment && {
        id: `enablePartialPayment-${row.id}`,
        label: 'Enable Partial Payment',
        onClick: (event: React.MouseEvent) =>
          onClickEnablePartialPayment(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-yellow60'
      })
    },
    {
      ...(row?.payment_link && {
        id: `copyPaymentLink-${row.id}`,
        label: isOrgLite ? 'Copy Payment Message' : 'Copy Payment Link',
        onClick: (event: React.MouseEvent) =>
          handleCopy(event, row?.payment_link),
        iconName: 'copy-primary',
        tooltipText: 'Copied!'
      })
    },
    {
      ...(showDisableIcon(row) && {
        id: `disable-${row.id}`,
        label: 'Disable',
        onClick: (event: React.MouseEvent) => onClickDisable(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-error40'
      })
    }
  ];

  const isActionButtonDisabled =
    row.status === 'disabled' ||
    (!hasPaymentOrderEditAccess &&
      !showToSendReminder &&
      !showReminderAlreadySent &&
      !showEnablePartialPayment &&
      !showDisableIcon(row));

  return { actions, isActionButtonDisabled };
};

const formattedDate = (date: string | Date, isEndDate = false) =>
  getFormattedDateTimeWithOffset(date, TimeZones['Asia/Kolkata'], isEndDate);

export const getDateRange = (
  selectedFilters: SelectedFilterValues,
  customFields: CustomField[]
) => {
  let selectedDate: DateRange;

  if (selectedFilters.dateRange[0] === '') {
    return {
      from_timestamp: '',
      to_timestamp: ''
    };
  }

  const customDate = customFields.find((field) => field.key === 'dateRange');

  switch (selectedFilters.dateRange[0]) {
    case 'current':
      selectedDate = getStartAndPresentDateOfCurrentMonth();
      break;
    case 'last':
      selectedDate = getStartAndEndDateOfLastMonth();
      break;
    case 'current_year':
      selectedDate = getCurrentFinancialYear();
      break;
    case 'last_year':
      selectedDate = getLastFinancialYear();
      break;
    default:
      if (customDate) {
        selectedDate = {
          startDate: new Date(customDate.value.startDate),
          endDate: new Date(customDate.value.endDate)
        };
      } else {
        selectedDate = {
          startDate: new Date(),
          endDate: new Date()
        };
      }
  }

  return {
    from_timestamp: formattedDate(selectedDate.startDate),
    to_timestamp: formattedDate(selectedDate.endDate, true)
  };
};

export const getSearchPlaceholder = (
  selectedSearchCategory: string,
  transactionCategories: DropdownOption[]
) => {
  const searchCategory = transactionCategories.find(
    (category) => category.value === selectedSearchCategory
  );
  return `Search ${searchCategory?.label}`;
};

export const quickCollectStatusConfig = (
  status: TransactionStatus,
  statusLabel = ''
) => {
  const statusConfigMap: Record<
    TransactionStatus,
    { label: string; className: string }
  > = {
    [TransactionStatus.DISABLED]: {
      label: 'Disabled',
      className: 'bg-grey10 text-bgGrey80'
    },
    [TransactionStatus.TO_SETTLE]: {
      label: 'Processing',
      className: 'bg-blue text-blue1'
    },
    [TransactionStatus.SETTLED]: {
      label: 'Paid',
      className: 'bg-primary10 text-primary40'
    },
    [TransactionStatus.PROCCESSING]: {
      label: 'Pending',
      className: 'bg-yellowLite4 text-yellow70'
    },
    [TransactionStatus.DEFAULT]: {
      label: statusLabel || 'Unknown',
      className: 'bg-cofeeGreyBlue100 text-cofeeNeutral8'
    }
  };

  return statusConfigMap[status] || statusConfigMap[TransactionStatus.DEFAULT];
};
